import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import Button from 'components/button';
import {
  INVOICE_LIST_STALE,
  fillText,
} from 'components/invoiceImport/invoiceImportText';
import { companyLabelOf } from 'components/invoiceDetail/invoiceDetailText';
import ModalBody from 'components/modal/components/body';
import ModalFooter from 'components/modal/components/footer';
import Modalheader from 'components/modal/components/header';
import useModal from 'components/modal/useModal';
import StyledInput from 'components/styledInput/StyledInput';
import { PAYMENT_TERMS, paymentTermLabel } from 'constants/paymentTerms';
import UseContext from 'hooks/useContext';
import ApiDatabase from 'server';
import {
  categoryOptionsOf,
  categoryValueOf,
  changedInformation,
  initialValuesOf,
  invoiceCategoryId,
  paymentTermKeyOf,
} from './invoiceInformationForm';

const SAVED_TEXTS = {
  updated: 'Enregistré dans Jobbiz et dans Pennylane.',
  on_import: 'Enregistré dans Jobbiz ; Pennylane le recevra à l’import.',
  skipped:
    'Enregistré dans Jobbiz ; Pennylane n’est pas modifié hors production.',
  unchanged: 'Enregistré dans Jobbiz ; rien à changer dans Pennylane.',
};

// The refusals the modal words; any other one gets the generic text.
const ERROR_TEXTS = {
  PENNYLANE_INVOICE_NOT_FOUND:
    'Cette facture est introuvable dans Pennylane : ce qui devait y être modifié n’est pas enregistré.',
  IMPORT_ALREADY_RUNNING:
    'Un import de ce mois est en cours : réessayez quand il sera terminé.',
  INVOICE_INFORMATION_SAVING:
    'Une autre facture de ce mois est en cours d’enregistrement : réessayez dans un instant.',
  INVOICE_WITHOUT_COMPANY:
    'Cette facture n’a pas d’entreprise : sa catégorie et son délai ne se modifient pas.',
};

const FIELD_TEXTS = {
  category: {
    saved: 'Catégorie : enregistrée.',
    failed:
      'Catégorie : non enregistrée, Pennylane l’a refusée ou n’a pas répondu.',
  },
  paymentTerm: {
    saved: 'Délai de paiement : enregistré.',
    failed:
      'Délai de paiement : non enregistré, Pennylane l’a refusé ou n’a pas répondu.',
  },
};

// The body's keys, named as the api's `failed` names them.
const SENT_FIELDS = [
  ['categoryId', 'category'],
  ['paymentTerm', 'paymentTerm'],
];

/**
 * « Informations de la facture » (JBWEB-1275, JBWEB-1276): the category and
 * the payment term of one invoice, against its company's. `modalData` is
 * `{ invoice, categories }`, as the detail panel holds them; `categories` is
 * null when the panel has not read them yet, and the modal reads them itself.
 * Sends only the fields changed, in one request.
 */
const ModalInvoiceInformation = () => {
  const { toggle } = useModal();
  const { language, modalData, setModalDataReturn } = UseContext();
  const text = (key, fallback) =>
    language.getValue(`invoiceInformation.${key}`, fallback);
  const detailText = (key, fallback) =>
    language.getValue(`invoiceDetail.${key}`, fallback);

  const [categories, setCategories] = useState(modalData?.categories ?? null);
  // The invoice the form compares with: a refusal that still wrote part of
  // the change answers with the invoice, which becomes the new reference.
  const [invoice, setInvoice] = useState(modalData?.invoice ?? null);
  const initial = initialValuesOf(invoice);
  const [categoryValue, setCategoryValue] = useState(initial.categoryValue);
  const [termKey, setTermKey] = useState(initial.termKey);
  const [isSending, setIsSending] = useState(false);
  const [failure, setFailure] = useState(null);

  useEffect(() => {
    if (categories !== null) return undefined;
    let active = true;
    ApiDatabase.getPennyLaneCategories(
      {},
      (data) => {
        if (active) setCategories(data?.items ?? []);
      },
      // Without the list, a category is named by its number.
      () => {},
    );
    return () => {
      active = false;
    };
  }, [categories]);

  const body = changedInformation(initial, { categoryValue, termKey });
  const hasChanges = Object.keys(body).length > 0;
  const canSave = hasChanges && !isSending;

  const company = invoice ? companyLabelOf(detailText, invoice) : '';
  const defaults = invoice?.companyDefaults ?? {};
  const companyCategoryValue = categoryValueOf(defaults.categoryId);
  const companyTermKey = paymentTermKeyOf(defaults.paymentTerm);
  const categoryOptions = categoryOptionsOf(
    categories,
    [invoiceCategoryId(invoice), defaults.categoryId],
    text,
  );

  const pickCategory = (value) => {
    setCategoryValue(value);
    setFailure(null);
  };
  const pickTerm = (value) => {
    setTermKey(value);
    setFailure(null);
  };

  // One return carries both effects of a write: the month's list reads
  // itself again on INVOICE_LIST_STALE, and the detail panel takes `invoice`.
  // Two returns in a row would leave only the last one.
  const announceWritten = (answered) =>
    setModalDataReturn({
      type: INVOICE_LIST_STALE,
      number: invoice.number,
      invoice: answered ?? null,
    });

  const showInvoice = (answered) => {
    if (!answered) return;
    setInvoice((previous) => ({ ...previous, ...answered }));
    announceWritten(answered);
  };

  const partialFailure = (failed, sent) => [
    text(
      'errors.partialTitle',
      'Pennylane n’a pas pris toutes les modifications.',
    ),
    ...SENT_FIELDS.filter(([key]) => key in sent).map(([, field]) => {
      const outcome = failed.includes(field) ? 'failed' : 'saved';
      return text(`errors.${field}.${outcome}`, FIELD_TEXTS[field][outcome]);
    }),
  ];

  const save = () => {
    if (!canSave) return;
    const sent = body;
    setIsSending(true);
    setFailure(null);
    ApiDatabase.putInvoiceInformation(
      { number: invoice.number, ...sent },
      (response) => {
        setIsSending(false);
        const sync = response?.pennylaneSync;
        const message = SAVED_TEXTS[sync]
          ? text(`saved.${sync}`, SAVED_TEXTS[sync])
          : text('saved.updated', SAVED_TEXTS.updated);
        toast.success(message);
        announceWritten(response?.invoice);
        toggle();
      },
      (error) => {
        if (error?.name === 'CanceledError') return;
        setIsSending(false);
        // Never the error object itself in the console: axios keeps the
        // request, the admin's token included.
        const status = error?.response?.status;
        const data = error?.response?.data ?? {};
        const code = data.errorCode;
        if (status === 502 && code === 'PENNYLANE_UPDATE_FAILED') {
          showInvoice(data.invoice);
          setFailure(
            partialFailure(Array.isArray(data.failed) ? data.failed : [], sent),
          );
          return;
        }
        if (status === 409 && ERROR_TEXTS[code]) {
          if (code === 'PENNYLANE_INVOICE_NOT_FOUND') showInvoice(data.invoice);
          setFailure([text(`errors.${code}`, ERROR_TEXTS[code])]);
          return;
        }
        setFailure([
          text('errors.generic', 'L’enregistrement a échoué, réessayez.'),
        ]);
      },
    );
  };

  const restoreButton = (onClick) => (
    <>
      {' · '}
      <button
        type={'button'}
        className={'underline text-gray-900'}
        onClick={onClick}
        disabled={isSending}
      >
        {text('restore', 'Rétablir')}
      </button>
    </>
  );

  const categoryHint = () => {
    if (defaults.categoryId == null) {
      return (
        <>
          {fillText(
            text(
              'companyWithoutCategory',
              '{company} n’a pas de catégorie par défaut.',
            ),
            { company },
          )}
          {categoryValue !== companyCategoryValue &&
            restoreButton(() => pickCategory(companyCategoryValue))}
        </>
      );
    }
    if (categoryValue === companyCategoryValue) {
      return text('categoryOfTheCompany', 'Catégorie de l’entreprise');
    }
    const companyCategory = categoryOptions.find(
      (option) => option.value === companyCategoryValue,
    );
    return (
      <>
        {fillText(
          text('categoryOfCompany', 'Catégorie de {company} : {name}'),
          {
            company,
            name: companyCategory?.label ?? '',
          },
        )}
        {restoreButton(() => pickCategory(companyCategoryValue))}
      </>
    );
  };

  const termHint = () => {
    const label = paymentTermLabel(defaults.paymentTerm, language);
    if (!label) return null;
    const term = defaults.paymentTermIsDefault
      ? fillText(text('termDefault', '{term} (par défaut)'), { term: label })
      : label;
    if (termKey === companyTermKey) {
      return defaults.paymentTermIsDefault
        ? fillText(
            text('termOfTheCompanyWithValue', 'Délai de l’entreprise : {term}'),
            { term },
          )
        : text('termOfTheCompany', 'Délai de l’entreprise');
    }
    return (
      <>
        {fillText(text('termOfCompany', 'Délai de {company} : {term}'), {
          company,
          term,
        })}
        {companyTermKey && restoreButton(() => pickTerm(companyTermKey))}
      </>
    );
  };

  if (!invoice) return null;

  return (
    <>
      <Modalheader hide={toggle} hideShow={!isSending}>
        {text('title', 'Informations de la facture')}
      </Modalheader>
      <ModalBody>
        <div className={'flex flex-col gap-4'}>
          <div className={'ft-sm text-gray-500'}>
            {fillText(
              text(
                'intro',
                'S’applique à la facture n° {number} seulement, pas aux autres factures de l’entreprise.',
              ),
              { number: invoice.number },
            )}
          </div>
          <div className={'flex flex-col gap-1'}>
            <StyledInput
              type={'select'}
              label={text('category', 'Catégorie')}
              value={categoryValue}
              list={categoryOptions}
              isDisabled={isSending}
              onChange={(event) => pickCategory(event.target.value)}
            />
            <div className={'text-gray-500 text-xs'}>{categoryHint()}</div>
          </div>
          <div className={'border-t border-gray-100'} />
          <div className={'flex flex-col gap-1'}>
            <StyledInput
              type={'select'}
              label={text('paymentTerm', 'Délai de paiement')}
              value={termKey}
              placeholder={text('paymentTermPlaceholder', 'Choisir un délai')}
              list={PAYMENT_TERMS.map((term) => ({
                value: term.key,
                label: paymentTermLabel(term, language),
              }))}
              isDisabled={isSending}
              onChange={(event) => pickTerm(event.target.value)}
            />
            <div className={'text-gray-500 text-xs'}>{termHint()}</div>
          </div>
          {failure && (
            <div
              role={'alert'}
              className={
                'flex flex-col gap-1 rounded-lg bg-red-50 px-4 py-3 text-sm text-red-700'
              }
            >
              {failure.map((line) => (
                <div key={line}>{line}</div>
              ))}
            </div>
          )}
        </div>
      </ModalBody>
      <ModalFooter>
        <Button
          size={'LG'}
          color={isSending ? 'DISABLED' : 'SECONDARY'}
          disabled={isSending}
          onClick={toggle}
        >
          {text('cancel', 'Annuler')}
        </Button>
        <Button
          size={'LG'}
          color={canSave ? 'PRIMARY' : 'DISABLED'}
          disabled={!canSave}
          onClick={save}
        >
          {isSending
            ? text('saving', 'Enregistrement…')
            : text('save', 'Enregistrer')}
        </Button>
      </ModalFooter>
    </>
  );
};

export default ModalInvoiceInformation;
